import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';

// Layouts
import AdminLayout from './layouts/AdminLayout';

// Pages
import LoginPage from './pages/LoginPage';
import SetupPage from './pages/SetupPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import TenantsPage from './pages/tenants/TenantsPage';
import TenantDetailPage from './pages/tenants/TenantDetailPage';
import NewTenantPage from './pages/tenants/NewTenantPage';
import SubscriptionsPage from './pages/subscriptions/SubscriptionsPage';
import PlansPage from './pages/subscriptions/PlansPage';
import InvoicesPage from './pages/subscriptions/InvoicesPage';
import LeadsPage from './pages/sales/LeadsPage';
import LeadDetailPage from './pages/sales/LeadDetailPage';
import OpportunitiesPage from './pages/sales/OpportunitiesPage';
import PipelinePage from './pages/sales/PipelinePage';
import ContactsPage from './pages/marketing/ContactsPage';
import ContactDetailPage from './pages/marketing/ContactDetailPage';
import ListsPage from './pages/marketing/ListsPage';
import TemplatesPage from './pages/marketing/TemplatesPage';
import CampaignsPage from './pages/marketing/CampaignsPage';
import AnalyticsPage from './pages/analytics/AnalyticsPage';
import AiInsightsPage from './pages/analytics/AiInsightsPage';
import SupportPage from './pages/support/SupportPage';
import TicketDetailPage from './pages/support/TicketDetailPage';
import SettingsPage from './pages/settings/SettingsPage';
import AdminUsersPage from './pages/settings/AdminUsersPage';
import AuditLogsPage from './pages/settings/AuditLogsPage';
import TermsConditionsPage from './pages/settings/TermsConditionsPage';
import LegalEntityPage from './pages/settings/LegalEntityPage';
import NotificationsPage from './pages/settings/NotificationsPage';
// TEMPORARY one-time encryption key export (remove after use)
import KeyExportPage from './pages/settings/KeyExportPage';
import SmsBillingPage from './pages/SmsBillingPage';

// Loading component
const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
  </div>
);

// Protected route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public route wrapper (redirects to dashboard if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function App() {
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/setup"
        element={
          <PublicRoute>
            <SetupPage />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <ResetPasswordPage />
          </PublicRoute>
        }
      />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />

        {/* Tenants */}
        <Route path="tenants" element={<TenantsPage />} />
        <Route path="tenants/new" element={<NewTenantPage />} />
        <Route path="tenants/:id" element={<TenantDetailPage />} />

        {/* Subscriptions */}
        <Route path="subscriptions" element={<SubscriptionsPage />} />
        <Route path="subscriptions/plans" element={<PlansPage />} />
        <Route path="subscriptions/addons" element={<Navigate to="/subscriptions/plans" replace />} />
        <Route path="subscriptions/invoices" element={<InvoicesPage />} />

        {/* Sales */}
        <Route path="sales/leads" element={<LeadsPage />} />
        <Route path="sales/leads/:id" element={<LeadDetailPage />} />
        <Route path="sales/opportunities" element={<OpportunitiesPage />} />
        <Route path="sales/pipeline" element={<PipelinePage />} />

        {/* Marketing */}
        <Route path="marketing/contacts" element={<ContactsPage />} />
        <Route path="marketing/contacts/:id" element={<ContactDetailPage />} />
        <Route path="marketing/lists" element={<ListsPage />} />
        <Route path="marketing/templates" element={<TemplatesPage />} />
        <Route path="marketing/campaigns" element={<CampaignsPage />} />
        <Route path="marketing/sms-billing" element={<SmsBillingPage />} />

        {/* Analytics */}
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="analytics/ai" element={<AiInsightsPage />} />

        {/* Support */}
        <Route path="support" element={<SupportPage />} />
        <Route path="support/tickets/:id" element={<TicketDetailPage />} />
        <Route path="support/:id" element={<TicketDetailPage />} />

        {/* Settings */}
        <Route path="settings" element={<SettingsPage />} />
        <Route path="settings/users" element={<AdminUsersPage />} />
        <Route path="settings/audit-logs" element={<AuditLogsPage />} />
        <Route path="settings/terms" element={<TermsConditionsPage />} />
        <Route path="settings/legal-entity" element={<LegalEntityPage />} />
        <Route path="settings/notifications" element={<NotificationsPage />} />
        {/* Old link kept working */}
        <Route path="settings/signup-notifications" element={<NotificationsPage />} />
        <Route path="settings/key-export" element={<KeyExportPage />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
